/**
 * The AI mission operator: a machine proposes a mission, a human approves it.
 *
 * Every endpoint here is on the proposal side of a gate. Nothing on this module
 * reaches an aircraft: `/turn` writes a proposal row, `/approve` records a
 * human decision, and only `/execute` — which the caller must reach through
 * that decision — moves anything. The split exists so that a persuasive model
 * reply and an uploaded route can never be the same event.
 *
 * Two rules the client has to carry, because the server enforces them and a
 * client that does not mirror them just produces confusing failures:
 *
 *   - `contentHash` is mandatory on approve. Approving is a statement about a
 *     specific set of waypoints, and a 409 here means the proposal changed
 *     under the reviewer. That is a real finding, not a retry.
 *   - A 503 from `/turn` means no model is configured. The message is written
 *     for an operator; show it rather than treating it as a crash.
 *
 * Follows @/api/ai.ts: long timeout, `label` on anything that writes.
 */

import {
  api,
  authHeaders,
  reportCall,
  resolveUrl,
  type ApiError,
  type ApiResult,
} from '@/api/client';
import type { Zone } from '@/stores/missionStore';
import type { DroneId, Waypoint } from '@/types/domain';

/**
 * A turn runs a dozen tool calls through a language model, so it is minutes
 * rather than the seconds a command takes.
 *
 * This has to sit ABOVE the server's own budget or the browser gives up on work
 * that is still running and still going to succeed: at 95s an operator watching
 * a three-aircraft plan got "No response after 95s" while the server went on to
 * finish it at 86-100s and store the proposal. The chain is
 * TURN_BUDGET_S (240s, app/routers/copilot_turn.py)
 *   < nginx proxy_read_timeout (300s, infra/nginx.conf)
 *   < this. Raise them in that order.
 */
const TURN_TIMEOUT_MS = 280_000;

/** Decisions and uploads are quick; only the model call is slow. */
const COPILOT_TIMEOUT_MS = 95_000;

export type ProposalVerdict = 'go' | 'caution' | 'no-go';
export type FactorSeverity = 'ok' | 'unknown' | 'caution' | 'no-go';
export type ProposalFleet = 'sim' | 'real';
export type ProposalStatus =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'superseded'
  | 'executed'
  | 'failed'
  | 'undone';

/** One judgement with the number behind it. `value` is null only when the input
 *  was missing, which is what makes the factor UNKNOWN rather than satisfied. */
export interface EvaluationFactor {
  name: string;
  severity: FactorSeverity;
  detail: string;
  value: number | null;
  unit: string;
  droneId: DroneId | null;
}

export interface ZoneViolation {
  zoneId: string;
  zoneName: string;
  kind: string;
  /** Which waypoint, or which leg's first waypoint when `onLeg`. */
  index: number;
  onLeg: boolean;
}

export interface ProposalEvaluation {
  verdict: ProposalVerdict;
  summary: string;
  governingFactor: EvaluationFactor | null;
  factors: EvaluationFactor[];
  unknownFactors: EvaluationFactor[];
  blockingFactors: EvaluationFactor[];
  routeLengthM: Record<string, number>;
  zoneViolations: Record<string, ZoneViolation[]>;
}

export interface ProposedMission {
  proposalId: string;
  fleet: ProposalFleet;
  routes: Record<DroneId, Waypoint[]>;
  zones: Zone[];
  launch: Waypoint | null;
  roles: Record<DroneId, string>;
  /** DISPLAY ONLY. The model's own words about its own plan. Never parsed here,
   *  and rendered attributed so it cannot read as a system assertion. */
  narrative: string;
}

export interface StoredProposal {
  proposalId: string;
  conversationId: string;
  revision: number;
  parentId: string | null;
  authorId: string;
  fleet: ProposalFleet;
  status: ProposalStatus;
  contentHash: string;
  proposal: ProposedMission;
  evaluation: ProposalEvaluation;
  hasRollback: boolean;
  createdAt: number;
  decidedBy: string | null;
  decidedAt: number | null;
  decisionNote: string | null;
}

/** One tool the operator ran on the way to the answer. `thought` is the model's
 *  stated reason for the call — provenance the review panel shows, not a log. */
export interface CopilotStep {
  tool: string;
  thought: string;
  args: Record<string, unknown>;
  result: unknown;
  ok: boolean;
}

export interface CopilotTurn {
  say: string;
  steps: CopilotStep[];
  complete: boolean;
  failure: string | null;
  /** Always present, even on a turn that produced no proposal — it is what
   *  keeps the next turn on the same thread. */
  conversationId: string;
  proposal: StoredProposal | null;
}

/** One aircraft's outcome from an upload or a rollback. */
export interface DroneOutcome {
  droneId: DroneId;
  ok: boolean;
  detail: string;
  waypointCount: number;
}

/**
 * What execute/undo actually did. `ok` is true only when every aircraft took
 * its mission; anything else is a partial, and `outcomes` is the only way to
 * tell which airframes are carrying what.
 */
export interface ExecutionReport {
  ok: boolean;
  outcomes: DroneOutcome[];
  stoppedEarly: boolean;
  message: string;
}

export interface ExecutionResult {
  proposal: StoredProposal;
  report: ExecutionReport;
}

export interface TurnRequest {
  message: string;
  conversationId?: string;
  /** Set when the turn is a revision of an existing proposal rather than a new
   *  one, so the server can chain revisions instead of orphaning them. */
  proposalId?: string;
}

export function copilotTurn(body: TurnRequest): Promise<ApiResult<CopilotTurn>> {
  return api.post<CopilotTurn>('/copilot/turn', body, {
    label: 'COPILOT TURN',
    timeoutMs: TURN_TIMEOUT_MS,
  });
}

/* ── the same turn, narrated ─────────────────────────────────────────────── */

/** A step as it arrives, with its position in the trace so a dropped frame is
 *  visible rather than silently shortening the list. */
export interface CopilotLiveStep extends CopilotStep {
  index: number;
}

export interface TurnStreamHandlers {
  onStep: (step: CopilotLiveStep) => void;
  /** The whole turn, in the same shape `copilotTurn` resolves with. Called
   *  exactly once, and never after `onError`. */
  onDone: (turn: CopilotTurn) => void;
  onError: (error: ApiError) => void;
  signal?: AbortSignal;
}

/**
 * Statuses that mean this server has no streaming route — an older API behind a
 * newer bundle. Retrying the buffered route is right here and wrong everywhere
 * else: a 429 or a 500 has already spent the operator's quota, and asking again
 * would spend a second tick to fail the same way.
 *
 * 401 is in the set because `request` is where the refresh-and-retry lives, so
 * bouncing to it recovers an expired session instead of dropping the turn.
 * Nothing has run server-side at that point — auth is checked before the model.
 */
const RETRY_UNBUFFERED = new Set([401, 404, 405, 501]);

interface SseFrame {
  event: string;
  data: string;
}

/**
 * One SSE frame, or null if it carried no data.
 *
 * Comment lines — the server's opening `: open` and its heartbeats — exist to
 * stop a proxy closing a connection that is merely thinking, and carry nothing.
 */
function parseFrame(raw: string): SseFrame | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
  }
  return data.length ? { event, data: data.join('\n') } : null;
}

function streamError(message: string, kind: ApiError['kind'] = 'server'): ApiError {
  return { kind, message, code: null, detail: null };
}

async function bufferedTurn(body: TurnRequest, handlers: TurnStreamHandlers): Promise<void> {
  const result = await copilotTurn(body);
  if (result.ok) handlers.onDone(result.data);
  else handlers.onError(result.error);
}

/**
 * Run a turn and report each tool call as the server makes it.
 *
 * A plan is ten to sixteen tool calls at ten to seventeen seconds each, so the
 * buffered route leaves an operator with a spinner for two to four minutes and
 * no way to tell a working turn from a hung one. Same body, same result: the
 * `done` event carries exactly what `copilotTurn` resolves with, so the caller
 * has one end state to handle whichever path it took.
 *
 * `EventSource` cannot do this — it is GET-only — so this reads the body itself,
 * and every frame boundary check is against the accumulated buffer rather than
 * one chunk: a `data:` line split across two network reads is the failure this
 * whole parser exists to survive.
 */
export async function copilotTurnStream(
  body: TurnRequest,
  handlers: TurnStreamHandlers,
): Promise<void> {
  if (typeof ReadableStream === 'undefined') {
    await bufferedTurn(body, handlers);
    return;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TURN_TIMEOUT_MS);
  const abortFromCaller = () => controller.abort();
  handlers.signal?.addEventListener('abort', abortFromCaller);

  const url = resolveUrl('/copilot/turn/stream');
  const startedAt = Date.now();
  let status = 0;
  let settled = false;
  /** Handed to `copilotTurn`, which logs its own activity entry. Logging again
   *  here would put two rows in the operator's log for one turn. */
  let delegated = false;
  /** Held on an object rather than in a `let`, so the activity entry in the
   *  `finally` reads the error the closure actually recorded. */
  const outcome: { error: ApiError | null } = { error: null };

  const finish = (error?: ApiError): void => {
    if (settled) return;
    settled = true;
    if (error) {
      outcome.error = error;
      handlers.onError(error);
    }
  };

  try {
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: authHeaders({ Accept: 'text/event-stream', 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch {
      // Never opened. The buffered route reports network failure in the one
      // shape the rest of the app already handles, so let it.
      delegated = true;
      settled = true;
      await bufferedTurn(body, handlers);
      return;
    }

    status = response.status;
    if (!response.ok || response.body === null) {
      if (RETRY_UNBUFFERED.has(response.status) || response.body === null) {
        delegated = true;
        settled = true;
        await bufferedTurn(body, handlers);
        return;
      }
      const text = await response.text().catch(() => '');
      finish(streamError(text.trim() || `The co-pilot refused the turn (HTTP ${status})`));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      for (;;) {
        const chunk = await reader.read();
        if (chunk.done) break;
        // Re-normalised over the whole buffer rather than the chunk: a CRLF
        // split across two reads would otherwise leave a stray carriage return
        // welded to the frame boundary and the frame would never be recognised.
        buffer = (buffer + decoder.decode(chunk.value, { stream: true })).replace(/\r\n/g, '\n');

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = parseFrame(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf('\n\n');
          if (!frame) continue;

          let payload: unknown;
          try {
            payload = JSON.parse(frame.data);
          } catch {
            // A frame we cannot read is not a turn we can report on. Dropping
            // it is safe: a stream that ends without `done` still fails below.
            continue;
          }

          if (frame.event === 'step') {
            handlers.onStep(payload as CopilotLiveStep);
          } else if (frame.event === 'done') {
            settled = true;
            handlers.onDone(payload as CopilotTurn);
          } else if (frame.event === 'error') {
            finish(
              streamError((payload as { message?: string }).message ?? 'The co-pilot stopped.'),
            );
          }
        }
        if (settled) break;
      }
    } catch {
      // The read itself threw: the 280 s cap firing, a reset socket, a caller
      // aborting. Letting this escape would reject a promise the dock awaits
      // and leave it busy forever — the worst version of the bug this route
      // exists to fix. It is the same outcome as a stream that simply stopped,
      // and it is reported as one below.
    } finally {
      await reader.cancel().catch(() => undefined);
    }

    // The connection closed with no verdict: a dropped link, a proxy that timed
    // the turn out, a tab that slept.
    finish(
      streamError(
        'The co-pilot connection ended before the plan did. Nothing was sent to any aircraft.',
        'network',
      ),
    );
  } finally {
    clearTimeout(timer);
    handlers.signal?.removeEventListener('abort', abortFromCaller);
    if (!delegated) {
      reportCall(
        {
          method: 'POST',
          path: url,
          status,
          ok: outcome.error === null,
          latencyMs: Math.round(Date.now() - startedAt),
          message: outcome.error?.message ?? '',
          label: 'COPILOT TURN',
        },
        outcome.error?.kind,
      );
    }
  }
}

export function listProposals(filter: {
  conversationId?: string;
  status?: ProposalStatus;
} = {}): Promise<ApiResult<{ proposals: StoredProposal[] }>> {
  return api.get<{ proposals: StoredProposal[] }>('/copilot/proposals', {
    query: { conversationId: filter.conversationId, status: filter.status },
    logged: false,
  });
}

export function getProposal(proposalId: string): Promise<ApiResult<StoredProposal>> {
  return api.get<StoredProposal>(`/copilot/proposals/${encodeURIComponent(proposalId)}`, {
    logged: false,
  });
}

/**
 * Record a human approval of one exact mission.
 *
 * `contentHash` is not optional and is not a nicety: it is what makes the
 * approval refer to the waypoints the reviewer read. A 409 means the stored
 * proposal moved on, and the correct response is to re-read it, not to retry.
 *
 * `overrideNoGo` is for approving past a NO-GO; without it the server answers
 * 409 rather than approving. The note is written into the decision row with the
 * approver's name, so the UI must not send the flag without one.
 */
export function approveProposal(
  proposalId: string,
  contentHash: string,
  options: { note?: string; overrideNoGo?: boolean } = {},
): Promise<ApiResult<StoredProposal>> {
  return api.post<StoredProposal>(
    `/copilot/proposals/${encodeURIComponent(proposalId)}/approve`,
    { contentHash, note: options.note, overrideNoGo: options.overrideNoGo },
    { label: `PROPOSAL APPROVE ${proposalId}` },
  );
}

export function rejectProposal(
  proposalId: string,
  note?: string,
): Promise<ApiResult<StoredProposal>> {
  return api.post<StoredProposal>(
    `/copilot/proposals/${encodeURIComponent(proposalId)}/reject`,
    { note },
    { label: `PROPOSAL REJECT ${proposalId}` },
  );
}

/**
 * Push an approved mission to the aircraft.
 *
 * `confirmCallsigns` is required by the server for a real fleet. The UI makes
 * the operator type them; sending a list they did not type would defeat the
 * only check that distinguishes a deliberate release from a misclick.
 */
export function executeProposal(
  proposalId: string,
  confirmCallsigns: string[] = [],
): Promise<ApiResult<ExecutionResult>> {
  return api.post<ExecutionResult>(
    `/copilot/proposals/${encodeURIComponent(proposalId)}/execute`,
    { confirmCallsigns },
    { label: `PROPOSAL EXECUTE ${proposalId}`, timeoutMs: COPILOT_TIMEOUT_MS },
  );
}

export function undoProposal(
  proposalId: string,
  note?: string,
): Promise<ApiResult<ExecutionResult>> {
  return api.post<ExecutionResult>(
    `/copilot/proposals/${encodeURIComponent(proposalId)}/undo`,
    { note },
    { label: `PROPOSAL UNDO ${proposalId}`, timeoutMs: COPILOT_TIMEOUT_MS },
  );
}
